import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReportsWorkspaceComponent } from './reports-workspace.component';
import { ReportContextOption } from '../../models/report-context-option';
import { TournamentService } from '../../services/tournament/tournament.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { ReportContextOptionsService } from '../../services/reports/report-context-options.service';

@Component({
    selector: 'app-round-reports',
    standalone: true,
    imports: [ReportsWorkspaceComponent],
    template: `
        <app-reports-workspace
            [helpText]="helpText"
            [initialContext]="context()"
            [roundOptions]="roundOptions()"
            [courseOptions]="courseOptions()">
        </app-reports-workspace>
    `
})
export class RoundReportsComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly tournamentService = inject(TournamentService);
    private readonly loggingService = inject(LoggingService);
    private readonly contextOptionsService = inject(ReportContextOptionsService);

    private activeTournamentKey: string | null = null;

    readonly helpText = 'Download reports scoped to the selected round. Context fields default from your current round selection.';

    readonly roundOptions = signal<ReportContextOption[]>([]);
    readonly courseOptions = signal<ReportContextOption[]>([]);

    readonly context = computed(() => {
        const routeRoundId = this.route.snapshot.paramMap.get('roundId');
        const queryCourseId = this.route.snapshot.queryParamMap.get('courseId');

        const selectedRound = this.tournamentService.selectedRoundSignal();
        const selectedTournament = this.tournamentService.selectedTournamentSignal();

        const tournamentId = selectedRound && selectedRound.tournamentId
            ? selectedRound.tournamentId
            : (selectedTournament ? selectedTournament.tournamentId || null : null);

        const roundId = routeRoundId ?? (selectedRound?.roundId ?? null);
        const courseId = queryCourseId ?? null;

        return {
            tournamentId,
            roundId,
            courseId
        };
    });

    constructor() {
        effect(() => {
            const tournament = this.tournamentService.selectedTournamentSignal();
            if (!tournament?.tournamentId) {
                this.activeTournamentKey = null;
                this.roundOptions.set([]);
                this.courseOptions.set([]);
                return;
            }

            const tournamentKey = String(tournament.tournamentId);
            this.activeTournamentKey = tournamentKey;

            this.contextOptionsService.getRoundOptions(tournamentKey)
                .then(options => {
                    if (this.activeTournamentKey === tournamentKey) {
                        this.roundOptions.set(options);
                    }
                })
                .catch(error => this.loggingService.error('RoundReportsComponent', 'Failed to load rounds for reports workspace', error));

            this.contextOptionsService.getCourseOptions(tournamentKey)
                .then(options => {
                    if (this.activeTournamentKey === tournamentKey) {
                        this.courseOptions.set(options);
                    }
                })
                .catch(error => this.loggingService.error('RoundReportsComponent', 'Failed to load courses for reports workspace', error));
        });
    }
}
